import { fetchRest, getApiBaseUrl } from '@/config/api'

export type LitigeRetourType = 'litige' | 'retour'
export type SupplierDocumentStatus = 'a_confirmer' | 'a_emettre' | 'emis' | 'non_requis'

export interface SupplierDocumentInfo {
  status: SupplierDocumentStatus
  reference: string | null
  commentId: number
}

export interface LitigeRetourFacture {
  id: number
  numero: string
  total_ttc: number
}

export const supplierDocumentLabels: Record<SupplierDocumentStatus, string> = {
  a_confirmer: 'Non renseignée',
  a_emettre: 'Non émise',
  emis: 'Émise',
  non_requis: 'Non requise',
}

export async function saveSupplierDocument(
  id: number,
  input: { status: SupplierDocumentStatus; reference?: string | null; expectedCommentId: number },
): Promise<SupplierDocumentInfo> {
  const response = await fetchRest(`${getApiBaseUrl()}/api/litiges-retours/${id}/supplier-document`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return readJson<SupplierDocumentInfo>(response)
}

export type LitigeRetourStatus =
  | 'en_attente'
  | 'en_traitement'
  | 'resolu'
  | 'rejete'

export interface SellerLitigeRetour {
  id: number
  orderId: number
  orderGroupId: number
  buyerName: string | null
  typeRequest: LitigeRetourType
  description: string
  status: LitigeRetourStatus
  dateCommande: string | null
  createdAt: string
  updatedAt: string
}

export interface SellerLitigeRetourPage {
  items: SellerLitigeRetour[]
  total: number
}

export interface LitigeRetourStats {
  total: number
  ouverts: number
  litiges: number
  retours: number
}

export interface LitigeRetourShippingInfo {
  priceHt: number | null
  priceSource?: 'actual_return' | 'order_current' | 'order_original' | 'simulation'
  carrierId: number | null
  carrierName: string | null
  /** Données d'une étiquette générée pour le retour SAV (jamais l'étiquette d'expédition). */
  returnLabel?: {
    url?: string | null
    labelUrl?: string | null
    trackingNumber?: string | null
    carrierCode?: string | null
    storageKey?: string | null
    generatedAt?: string | null
  } | null
  returnTrackingEntries?: Array<{
    number: string
    carrierId: number | null
    url?: string | null
  }>
  /** @deprecated Legacy order-shipment tracking; SAV UI must use returnTrackingEntries. */
  trackingEntries: Array<{
    number: string
    carrierId: number | null
  }>
  simulated: boolean
  transport: ReturnTransportInfo
  transportAvailable: boolean
  unavailableReason?: string
  billingStatus?: 'non_eligible' | 'a_facturer' | 'facturee'
}

export type ReturnShippingCostPayer = 'a_definir' | 'plateforme' | 'grossiste' | 'acheteur'
export type ReturnReceiptStatus = 'en_attente' | 'recu' | 'non_recu'
export type SavBillingStatus = 'non_eligible' | 'a_facturer' | 'facturee'

export interface ReturnTransportInfo {
  shippingCostPayer: ReturnShippingCostPayer
  receiptStatus: ReturnReceiptStatus
  actualShippingAmountHt: number | null
  invoiceAmountHt: number | null
  invoiceDate: string | null
  sellerInvoiceId?: number | null
  billedAt?: string | null
}

export const returnShippingPayerLabels: Record<ReturnShippingCostPayer, string> = {
  a_definir: 'À définir',
  plateforme: 'Plateforme eFashion',
  grossiste: 'Grossiste',
  acheteur: 'Acheteur',
}

export const returnReceiptStatusLabels: Record<ReturnReceiptStatus, string> = {
  en_attente: 'En attente de confirmation',
  recu: 'Reçu',
  non_recu: 'Non reçu / étiquette non utilisée',
}

export const savBillingStatusLabels: Record<SavBillingStatus, string> = {
  non_eligible: 'Non éligible',
  a_facturer: 'À facturer',
  facturee: 'Facturée',
}

export const emptyReturnTransport = (): ReturnTransportInfo => ({
  shippingCostPayer: 'a_definir',
  receiptStatus: 'en_attente',
  actualShippingAmountHt: null,
  invoiceAmountHt: null,
  invoiceDate: null,
  sellerInvoiceId: null,
  billedAt: null,
})

export async function saveReturnTransport(id: number, input: ReturnTransportInfo): Promise<ReturnTransportInfo> {
  const response = await fetchRest(`${getApiBaseUrl()}/api/litiges-retours/${id}/return-transport`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return readJson<ReturnTransportInfo>(response)
}

export interface ReturnLabelResult {
  url: string
  trackingNumber: string
  carrierId: number | null
  carrierCode?: string | null
}

export async function generateReturnLabel(id: number): Promise<ReturnLabelResult> {
  const response = await fetchRest(`${getApiBaseUrl()}/api/litiges-retours/${id}/return-label`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  return readJson<ReturnLabelResult>(response)
}

export async function saveRefundAmount(id: number, refundAmount: number | null): Promise<void> {
  const response = await fetchRest(`${getApiBaseUrl()}/api/litiges-retours/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refundAmount }),
  })
  await readJson(response)
}

export interface LitigeRefundResult {
  msg: boolean
  pending?: boolean
  message?: string
  amount?: number
  refundSessionId?: string
}


export async function refundLitigeRetour(
  id: number,
  refundSessionId?: string,
): Promise<LitigeRefundResult> {
  const response = await fetchRest(`${getApiBaseUrl()}/api/admin-efp/ruptures/refund-litige`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      litigeRetourId: id,
      ...(refundSessionId ? { refundSessionId } : {}),
    }),
  })
  return readJson<LitigeRefundResult>(response)
}

export async function generateFactureFromLitigeRetour(id: number): Promise<LitigeRetourFacture> {
  const response = await fetchRest(`${getApiBaseUrl()}/api/litiges-retours/${id}/facture`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  return readJson<LitigeRetourFacture>(response)
}

export interface SellerLitigeRetourFilters {
  page: number
  limit: number
}

const baseUrl = () => `${getApiBaseUrl()}/api/seller/litiges-retours`

async function readJson<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    const message = (data as { message?: string | string[] }).message
    throw new Error(
      Array.isArray(message)
        ? message.join(', ')
        : message || `Erreur HTTP ${response.status}`,
    )
  }
  return data as T
}

export async function fetchSellerLitigesRetours(
  filters: SellerLitigeRetourFilters,
): Promise<SellerLitigeRetourPage> {
  const params = new URLSearchParams({
    page: String(filters.page),
    limit: String(filters.limit),
  })

  const response = await fetchRest(`${baseUrl()}?${params.toString()}`)
  return readJson<SellerLitigeRetourPage>(response)
}

export async function fetchSellerLitigeRetourStats(): Promise<LitigeRetourStats> {
  const response = await fetchRest(`${baseUrl()}/stats`)
  return readJson<LitigeRetourStats>(response)
}
